/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import {
    BookmarksCollectionSchedulePostRequest,
    BookmarksScheduleDeleteV2Request,
    BookmarksSchedulePostV2Request,
    Configuration,
    ConfigurationParameters,
    DefaultApi,
    SeasonOrYear,
} from 'client-code/bookmarks';

import { CommonHeaders } from '../CommonHeaders';
import { Constants } from '../constants';
import { IBookmarkServiceRepository } from '../interfaces/IBookmarkServiceRepository';
import { Log } from '../logging/Log';
import { TServicesParams } from '../types';

export class BookmarkServiceRepositoryImpl implements IBookmarkServiceRepository {
    private static TAG: string = 'BookmarkServiceRepositoryImpl';

    private mCommonHeaders: CommonHeaders;

    private mServiceParams: TServicesParams;

    private mBookmarkApi: DefaultApi;

    public constructor(params: TServicesParams) {
        this.mCommonHeaders = new CommonHeaders('Bookmark');
        this.mServiceParams = params;

        const oConfigParams: ConfigurationParameters = {
            basePath: this.mServiceParams.basePath,
            apiKey: this.mServiceParams.accessToken,
            baseOptions: {
                timeout: Constants.SERVICE_CONNECTION_TIMEOUT,
                headers: this.mCommonHeaders.headers,
            },
        };
        const configuration = new Configuration(oConfigParams);
        this.mBookmarkApi = new DefaultApi(configuration);
    }

    private getCommonRequestParams() {
        return {
            applicationName: this.mCommonHeaders.headers.ApplicationName,
            applicationFeatureArea: this.mCommonHeaders.headers.ApplicationFeatureArea,
            applicationVersion: this.mCommonHeaders.headers.ApplicationVersion,
            deviceType: this.mCommonHeaders.headers.DeviceType,
            productName: this.mCommonHeaders.headers.ProductName,
            bodyId: this.mServiceParams.bodyId,
            timeZoneName: this.mServiceParams.timeZone,
            whiny: this.mServiceParams.whiny || 'false',
        };
    }

    /**
     * @inheritdoc
     */
    public async createBookmarkContentIdSchedule(contentId: string, unifiedCollectionId: string) {
        const requestParameters: BookmarksSchedulePostV2Request = {
            ...this.getCommonRequestParams(),
            contentId,
            unifiedCollectionId,
        };
        try {
            return await this.mBookmarkApi.bookmarksSchedulePostV2(requestParameters);
        } catch (error) {
            Log.e(
                BookmarkServiceRepositoryImpl.TAG,
                'BookmarkRepositoryImpl::createBookmarkContentIdSchedule',
                error,
            );
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public async createBookmarkCollectionIdSchedule(
        collectionId: string,
        seasonOrYear: SeasonOrYear,
    ) {
        const requestParameters: BookmarksCollectionSchedulePostRequest = {
            ...this.getCommonRequestParams(),
            collectionId,
            seasonOrYear,
        };
        try {
            return await this.mBookmarkApi.bookmarksCollectionSchedulePost(requestParameters);
        } catch (error) {
            Log.e(
                BookmarkServiceRepositoryImpl.TAG,
                'BookmarkRepositoryImpl::createBookmarkCollectionIdSchedule',
                error,
            );
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public async deleteBookmarkContentIdSchedule(contentId: string) {
        const requestParameters: BookmarksScheduleDeleteV2Request = {
            ...this.getCommonRequestParams(),
            contentId,
        };
        try {
            return await this.mBookmarkApi.bookmarksScheduleDeleteV2(requestParameters);
        } catch (error) {
            Log.e(
                BookmarkServiceRepositoryImpl.TAG,
                'BookmarkRepositoryImpl::deleteBookmarkContentIdSchedule',
            );
            throw error;
        }
    }
}
